import { MiniMap, MiniMapProps } from "@/components/r3f/utils/minimap/minimap";
import { useAppSelector } from "@/store/store-hooks";
import { SceneFilter } from "@/types/scene-filter";
import {
  IElementImg360,
  isIElementImg2d,
  isIElementImg360,
} from "@faro-lotv/ielement-types";
import { SupportedCamera } from "@faro-lotv/lotv";
import { Vector3 as Vector3Prop } from "@react-three/fiber";
import { JSX, useCallback } from "react";
import { Vector3 } from "three";
import { SyncCamerasElements } from "../walk-mode/hooks/use-sync-cameras";
import { selectIsPanoAligned } from "../walk-mode/walk-scene-selectors";
import { WalkSceneActiveElement } from "../walk-mode/walk-types";

export type MoveBothCamerasFromMiniMap = (el: IElementImg360) => void;

type SplitMiniMapProps = Pick<
  MiniMapProps,
  "trackingElement" | "canvasElement" | "actions" | "onPlaceholderClicked"
> & {
  /** Main walk element */
  mainWalkElement: WalkSceneActiveElement;
  /** Compare walk element */
  compareWalkElement: WalkSceneActiveElement;
  /** Main scene filter */
  mainSceneFilter: SceneFilter;
  /** Compare scene filter */
  compareSceneFilter: SceneFilter;
  /** Camera used in the left view */
  leftCamera: SupportedCamera;
  /** Camera used in the right view */
  rightCamera: SupportedCamera;
  /** Synchronization object for cameras */
  syncCameras?: SyncCamerasElements;
  /** Position of the minimap camera */
  miniMapCameraPosition: Vector3Prop | undefined;
  /** Callback issued when the left camera should be animated to a new position */
  onLeftCameraMovedTo?(pos: Vector3): void;
  /** Callback issued when the right camera should be animated to a new position */
  onRightCameraMovedTo?(pos: Vector3): void;
};

/**
 * The minimap specialized for splitscreen mode. This component is an extension of the basic Minimap component. It provides
 * synchronization of the two camera positions when the minimap is clicked. It also manages the visualization of the two
 * cones representing the field of views of the left and right camera.
 *
 * @returns a component that instantiates the minimap in splitscreen mode
 */
export function SplitMinMap({
  trackingElement,
  canvasElement,
  actions,
  mainWalkElement,
  compareWalkElement,
  mainSceneFilter,
  compareSceneFilter,
  leftCamera,
  rightCamera,
  syncCameras,
  miniMapCameraPosition,
  onPlaceholderClicked,
  onLeftCameraMovedTo,
  onRightCameraMovedTo,
}: SplitMiniMapProps): JSX.Element {
  const onMinimapClicked = useCallback(
    (pos: Vector3) => {
      if (mainSceneFilter !== SceneFilter.Pano) {
        // Keep the camera height as it is
        pos.y = leftCamera.position.y;
        syncCameras?.setLeftCameraPosition(pos);
        onLeftCameraMovedTo?.(pos);
      }

      if (compareSceneFilter !== SceneFilter.Pano) {
        // Keep the camera height as it is
        pos.y = rightCamera.position.y;
        syncCameras?.setRightCameraPosition(pos);
        onRightCameraMovedTo?.(pos);
      }
    },
    [
      mainSceneFilter,
      compareSceneFilter,
      leftCamera.position.y,
      syncCameras,
      onLeftCameraMovedTo,
      rightCamera.position.y,
      onRightCameraMovedTo,
    ],
  );

  const onTrajectoryPointClicked = useCallback(
    (eyePosition: Vector3) => {
      if (mainSceneFilter !== SceneFilter.Pano) {
        syncCameras?.setLeftCameraPosition(eyePosition);
        onLeftCameraMovedTo?.(eyePosition);
      }
      if (compareSceneFilter !== SceneFilter.Pano) {
        syncCameras?.setRightCameraPosition(eyePosition);
        onRightCameraMovedTo?.(eyePosition);
      }
    },
    [
      compareSceneFilter,
      mainSceneFilter,
      syncCameras,
      onLeftCameraMovedTo,
      onRightCameraMovedTo,
    ],
  );

  const isMainElementAligned = useAppSelector(
    selectIsPanoAligned(mainWalkElement),
  );
  const isCompareElementAligned = useAppSelector(
    selectIsPanoAligned(compareWalkElement),
  );

  const shouldShowViewCone =
    !isIElementImg2d(mainWalkElement) &&
    (!isIElementImg360(mainWalkElement) || isMainElementAligned);
  const shouldShowViewConeSecondCamera =
    !isIElementImg2d(compareWalkElement) &&
    (!isIElementImg360(compareWalkElement) || isCompareElementAligned);

  return (
    <MiniMap
      actions={actions}
      canvasElement={canvasElement}
      trackingElement={trackingElement}
      camera={leftCamera}
      secondScreenCamera={rightCamera}
      cameraPosition={miniMapCameraPosition ?? leftCamera.position}
      showUserMarker
      shouldShowViewCone={shouldShowViewCone}
      shouldShowViewConeSecondCamera={shouldShowViewConeSecondCamera}
      onPlaceholderClicked={onPlaceholderClicked}
      onMinimapClicked={onMinimapClicked}
      onTrajectoryPointClicked={onTrajectoryPointClicked}
    />
  );
}
